import { useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import { StyleSheet, View } from "react-native";

import { AddItemModal } from "~/features/items/AddItemModal";
import { categoryLabel, statusLabel } from "~/features/items/labels";
import {
  AppText,
  Badge,
  Button,
  ButtonText,
  Card,
  EmptyState,
  ListRow,
  ProtectedScreen,
  SectionHeader,
  Stack,
  TextField,
} from "~/components/ui";
import { usePlan } from "~/hooks/usePlan";
import { useItems } from "~/state/items";
import { useKit, useKitActions } from "~/state/kits";
import type { Kit } from "~/state/sync";
import { spacing } from "~/styles/theme";

export default function KitDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const kit = useKit(id);

  if (!kit) {
    return (
      <ProtectedScreen title="Kit">
        <EmptyState
          title="Kit not found"
          description="It may have been deleted on another device."
        />
      </ProtectedScreen>
    );
  }

  return <KitDetail kit={kit} />;
}

function KitDetail({ kit }: { kit: Kit }) {
  const router = useRouter();
  const plan = usePlan();
  const items = useItems(kit.id);
  const actions = useKitActions();
  const [addOpen, setAddOpen] = useState(false);

  return (
    <ProtectedScreen title={kit.name} subtitle={kit.notes ?? undefined}>
      <Card gap="md">
        <SectionHeader
          title={`${items.length} ${items.length === 1 ? "item" : "items"}`}
          description="Tap an item to edit specs, purchase details, and attachments."
        />
        {items.length === 0 ? (
          <EmptyState
            title="Empty kit"
            description="Add the first tool or device that belongs here."
          />
        ) : (
          <Stack gap="none">
            {items.map((item, index) => (
              <ListRow
                key={item.id}
                first={index === 0}
                title={item.name}
                description={[item.brand, item.model].filter(Boolean).join(" ")}
                onPress={() => router.push(`/items/${item.id}` as never)}
                meta={
                  <>
                    <Badge tone="neutral">{categoryLabel(item.category)}</Badge>
                    <Badge tone="neutral">{statusLabel(item.status)}</Badge>
                  </>
                }
              />
            ))}
          </Stack>
        )}
        <Button
          align="start"
          iconName="plus"
          disabled={!plan.items.allowed}
          onPress={() => setAddOpen(true)}
        >
          <ButtonText>Add item</ButtonText>
        </Button>
        {!plan.items.allowed ? (
          <AppText size="xs">{plan.items.message}</AppText>
        ) : null}
      </Card>

      <Card gap="md">
        <SectionHeader title="Kit details" />
        <View style={stylesheet.field}>
          <AppText size="xs" weight="semibold">
            Name
          </AppText>
          <TextField
            accessibilityLabel="Kit name"
            value={kit.name}
            onChangeText={(value) => actions.update(kit.id, { name: value })}
          />
        </View>
        <View style={stylesheet.field}>
          <AppText size="xs" weight="semibold">
            Notes
          </AppText>
          <TextField
            accessibilityLabel="Kit notes"
            value={kit.notes ?? ""}
            multiline
            style={stylesheet.textarea}
            onChangeText={(value) => actions.update(kit.id, { notes: value })}
          />
        </View>
        <Button
          variant="outline"
          onPress={() => {
            actions.remove(kit.id);
            router.replace("/kits" as never);
          }}
        >
          <ButtonText>Delete kit</ButtonText>
        </Button>
      </Card>

      <AddItemModal
        visible={addOpen}
        kitId={kit.id}
        onClose={() => setAddOpen(false)}
        onCreated={(itemId) => {
          setAddOpen(false);
          router.push(`/items/${itemId}` as never);
        }}
      />
    </ProtectedScreen>
  );
}

const stylesheet = StyleSheet.create({
  field: { gap: spacing.xs },
  textarea: { minHeight: 86, textAlignVertical: "top" },
});
